<template>
  <span :key="getFieldID(schema)">
    <template
      v-if="
        schema.attributes.editing &&
        !disabledForAdminOnly &&
        !selectOptions.displayAsRadio
      ">
      <select
        class="form-select"
        v-model="value"
        :disabled="disabled || null"
        :nameId="schema.inputName"
        :id="getFieldID(schema)"
        :class="schema.fieldClasses"
      >
        <option
          v-if="!selectOptions.hideNoneSelectedText"
          :disabled="schema.required || null"
          :value="null"
        >
          {{ selectOptions.noneSelectedText || "&lt;Nothing selected&gt;" }}
        </option>

        <template v-for="item in items">
          <optgroup
            v-if="item.group"
            :label="getGroupName(item)"
            :key="getGroupName(item)"
          >
            <template v-if="item.ops">
              <option
                v-for="i in item.ops"
                :value="getItemValue(i)"
                :key="getItemValue(i)"
              >
                {{ getItemName(i) }}
              </option>
            </template>
          </optgroup>
          <option
            v-if="!item.group"
            :value="getItemValue(item)"
            :key="getItemValue(item)"
          >
            {{ getItemName(item) }}
          </option>
        </template>
      </select>
    </template>
    <template
      v-else-if="
        schema.attributes.editing &&
        !disabledForAdminOnly &&
        selectOptions.displayAsRadio
      ">
      <div class="form-group no-required-asterisk">
        <label
          v-if="!selectOptions.hideNoneSelectedText"
          class="form-radio none-radio-option"
          :disabled="schema.required || null">
          <input
            type="radio"
            :name="`name-${getFieldID(schema)}`"
            v-model="value"
            :value="null">
          <i class="form-icon"></i>
          {{ selectOptions.noneSelectedText || "&lt;Nothing selected&gt;" }}
        </label>
        <template v-for="item in items">
          <template v-if="item.group">
            <!-- TODO, we don't currently use this feature from VFG. -->
          </template>
          <template v-else>
            <label
              class="form-radio">
              <input
                type="radio"
                :name="`name-${getFieldID(schema)}`"
                v-model="value"
                :value="getItemValue(item)">
              <i class="form-icon"></i> {{ getItemName(item) }}
            </label>
          </template>
        </template>
      </div>
    </template>
    <template v-else>
      <span v-if="!lodash.isNull(value)" class="response">
        {{ value }}
      </span>
      <span
        v-else-if="!schema.attributes.editing"
        style="font-style: italic; color: #555">
        Not available
      </span>
    </template>
    <div
      class="form-input-hint text-error"
      v-if="schema.attributes.editing && disabledForAdminOnly">
      Field is restricted to site administrators.
    </div>
  </span>
</template>

<script>
// This is a lightly modified version of vue-form-generator's select field.
import _ from 'lodash';

import { MixinDataToReactiveData } from 'assets/js/util';

import { abstractField } from 'components/Widgets/FormGenerator';
import FieldCommon from './FieldCommon';

export default {
  name: 'field-q-select',

  mixins: [abstractField, FieldCommon],

  setup() {
    const afd = MixinDataToReactiveData(abstractField);
    const fcd = MixinDataToReactiveData(FieldCommon);
    return {
      ...afd,
      ...fcd,
    };
  },

  computed: {
    items() {
      const values = this.schema.values;
      if (typeof values === 'function') {
        return this.groupValues(values.apply(this,
          [this.model, this.schema]));
      }
      return this.groupValues(values);
    },

    selectOptions() {
      return this.schema.selectOptions || {};
    },
  },

  methods: {
    groupValues(values) {
      if (!values) return [];
      const array = [];
      let arrayElement = {};
      values.forEach((item) => {
        arrayElement = null;
        if (item.group && _.isObject(item)) {
          // There is in a group.
          // Find element with this group.
          arrayElement = _.find(array, (i) => i.group === item.group);
          if (arrayElement) {
            // There is such a group.
            arrayElement.ops.push({
              id: item.id,
              name: item.name,
            });
          } else {
            // There is not such a group.
            // Initialising.
            arrayElement = {
              group: '',
              ops: [],
            };
            // Set group.
            arrayElement.group = item.group;
            // Set Group element.
            arrayElement.ops.push({
              id: item.id,
              name: item.name,
            });
            // Add array.
            array.push(arrayElement);
          }
        } else {
          // There is not in a group.
          array.push(item);
        }
      });
      // With Groups.
      return array;
    },

    getGroupName(item) {
      if (item && item.group) {
        return item.group;
      }
      throw new Error('Group name is missing! https://icebob.gitbooks.io/'
          + 'vueformgenerator/content/fields/select.html#select-field-with-'
          + 'object-items');
    },

    getItemValue(item) {
      if (_.isObject(item)) {
        if (typeof this.schema['selectOptions'] !== 'undefined'
            && typeof this.schema['selectOptions']['value'] !== 'undefined') {
          return item[this.schema.selectOptions.value];
        }
        // Use 'id' instead of 'value' cause of backward compatibility
        if (typeof item['id'] !== 'undefined') {
          return item.id;
        }
        throw new Error('`id` is not defined. If you want to use another key '
            + 'name, add a `value` property under `selectOptions` in the '
            + 'schema. https://icebob.gitbooks.io/vueformgenerator/content/'
            + 'fields/select.html#select-field-with-object-items');
      } else {
        return item;
      }
    },

    getItemName(item) {
      if (_.isObject(item)) {
        if (typeof this.schema['selectOptions'] !== 'undefined'
            && typeof this.schema['selectOptions']['name'] !== 'undefined') {
          return item[this.schema.selectOptions.name];
        }
        if (typeof item['name'] !== 'undefined') {
          return item.name;
        }
        throw new Error('`name` is not defined. If you want to use another '
            + 'key name, add a `name` property under `selectOptions` in the '
            + 'schema. https://icebob.gitbooks.io/vueformgenerator/content/'
            + 'fields/select.html#select-field-with-object-items');
      } else {
        return item;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.none-radio-option {
  font-style: italic;
  font-weight: 400;
}

.no-required-asterisk {
  label::after {
    content: "" !important;
  }
}
</style>
